import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "../../../store";
import { GridCellParams } from "@mui/x-data-grid";
import { Base } from "../../base";
import JobGroup from "../../../types/jobGroup";
import ColumnDef from "../../../types/columnDef";
import AddHoursForm from "../../forms/AddHoursForm";
import AddHoursFormValues from "../../../types/addHoursFormValues";
import AddHoursVM from "../../../types/addHoursVM";
import SearchParams from "../../../types/searchParams";
import {
  AssignmentInd,
  Room,
  TableRows,
  ViewColumn,
} from "@mui/icons-material";
import moment from "moment";
import OpenJobList from "../../../types/openJobList";
import CompleteJobList from "../../../types/completeJobList";
import IconSwitcher, { FilledIconName } from "../../base/IconSwitcher";
import { AnswerTypes } from "../../../types/questionVM";
import { AnswerSite } from "../../../types/answerVM";
import { postAttachment } from "../../../slices/attachment";
import Attachment from "../../../types/attachment";
import JobAttachmentCopy from "../JobDashboard/SpecificViews/JobAttachmentCopy";

interface Props {
  onClickJob: (id: number) => void;
  onClickViewJob: (id: number) => void;
  onClickNewJob: () => void;
  apiGetAllUsers: () => void;
  apiCategorySearch: (search: SearchParams) => void;
  apiAddHours: (hours: AddHoursVM) => void;
  apiGetJob: (id: number) => void;
  openJobsList?: OpenJobList;
  completeJobsList?: CompleteJobList;
}

const OpenJobs: React.FC<Props> = (props) => {
  const { apiAddHours, apiGetJob, openJobsList, completeJobsList } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const { status, openJobStatus, closedJobStatus } = useSelector(
    (store) => store.job,
  );
  const { currentViewJobType } = useSelector((store) => store.jobType);
  const { status: attachmentStatus } = useSelector(
    (store) => store.attachments,
  );
  const { job } = useSelector((store) => store.job);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const sm = useMediaQuery(theme.breakpoints.down("md"));
  const md = useMediaQuery(theme.breakpoints.up("sm"));
  const [addHoursModal, setAddHoursModal] = useState<number | undefined>(
    undefined,
  );
  const [tableDirection, setTableDirection] = useState<"row" | "column">("row");
  const [viewSites, setViewSites] = useState<
    { name: string; address: string }[]
  >([]);
  const [upload, setUpload] = useState<{ id?: number; name: string }>({
    id: undefined,
    name: "",
  });
  const [newUpload, setNewUpload] = useState<Attachment[]>([]);
  const [fileLength, setFileLength] = useState<number>(0);
  const [fileTypeModal, setFileTypeModal] = useState<boolean>(false);

  const titleColumn: ColumnDef[] = [
    {
      field: "title",
      headerName: `${
        currentViewJobType?.questions.find(
          (q) => q.answerType.id === AnswerTypes.Title,
        )?.questionText ?? "Title"
      }`,
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "created",
      headerName: "Date Created",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "workedBy",
      headerName: "Worked By",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
      renderCell: (params) => <>{params.value}</>,
    },
  ];

  const siteColumn: ColumnDef[] = [
    ...titleColumn,
    {
      field: "site",
      headerName: "Site",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
  ];

  const actionsColumn: ColumnDef = {
    field: "actions",
    headerName: "Actions",
    headerAlign: "center",
    align: "center",
    flex: 1,
    headerClassName: "header",
    sortable: false,
    disableColumnMenu: true,
    disableExport: true,
    filterable: false,
    renderCell: (params) => <>{params.value}</>,
    mobileSize: 12,
  };

  const displayRows = (array: any) => {
    return array.map((job: JobGroup) => {
      let rowObject = {
        id: job.id,
        title: job.title === "" ? "Title" : job.title,
        site: job.id > 0 ? job.siteName ?? "N/A" : "",
        mobileTitle: `${job.title}`,
        created:
          job.id > 0 ? `${moment(job.createdOn).format("MM/DD/YYYY")}` : "",
        workedBy:
          job.id > 0 ? (
            job.assignedTo?.length ? (
              <Tooltip
                title={
                  <Box>
                    {job.assignedTo?.length ? (
                      job.assignedTo?.map((user, index) => (
                        <Box key={index}>
                          <Base.Text text={user} />
                        </Box>
                      ))
                    ) : (
                      <Base.Text text="None" />
                    )}
                  </Box>
                }
                sx={{ color: currentViewJobType?.color }}
              >
                <AssignmentInd color={"inherit"} fontSize="large" />
              </Tooltip>
            ) : (
              "None"
            )
          ) : (
            ""
          ),
        completed: !!job.completedOn,
        actions: (
          <Base.TableActions
            id={job.id}
            handleAddHours={
              job.title && !job.completedOn && currentViewJobType?.logHours
                ? () => {
                    setAddHoursModal(job.id);
                    apiGetJob(job.id);
                  }
                : undefined
            }
            handleView={
              !sm
                ? undefined
                : () => {
                    props.onClickViewJob(job.id);
                  }
            }
            handleUpload={
              job.title
                ? () => {
                    setUpload({ id: job.id, name: job.title! });
                  }
                : undefined
            }
            handleAddressRoute={
              job.answers &&
              job.answers.some((a) => Array.isArray(a.value)) &&
              job.answers.some((a) =>
                (a.value as AnswerSite[]).some((s) => !!s.address),
              )
                ? () => {
                    job.answers.forEach((a) => {
                      let list: { name: string; address: string }[] = [];
                      if (Array.isArray(a.value)) {
                        if ((a.value as AnswerSite[]).length)
                          (a.value as AnswerSite[]).forEach((s) => {
                            if (!!s.address)
                              list.push({ name: s.value!, address: s.address });
                          });
                      }
                      setViewSites(list);
                    });
                  }
                : undefined
            }
          />
        ),
      };
      return rowObject;
    });
  };

  const onCellClick = (params: any) => {
    if (params.field !== "actions" && params.field !== "workedBy") {
      if (params.row.id > 0) {
        if (params.row.completed) {
          props.onClickViewJob(params.row.id);
        } else {
          props.onClickJob(params.row.id);
        }
      }
    }
  };

  const tableDisplay = (
    title: string,
    jobArray: JobGroup[],
    expanded: boolean,
    badgeCount: number,
    statusId?: number,
  ) => {
    return (
      <Grid
        size={{
          md:
            tableDirection === "column"
              ? 12
              : openJobsList?.statusJobGroups.length === 1 &&
                !currentViewJobType?.showRecentlyClosed
              ? 12
              : (openJobsList?.statusJobGroups.length === 1 &&
                  currentViewJobType?.showRecentlyClosed) ||
                (openJobsList?.statusJobGroups.length === 2 &&
                  !currentViewJobType?.showRecentlyClosed)
              ? 6
              : 4,
          xs: 12,
        }}
        sx={{
          "& .header": {
            backgroundColor:
              !!currentViewJobType?.statuses?.find((s) => s.id === statusId)
                ?.iconColor &&
              currentViewJobType?.statuses?.find((s) => s.id === statusId)
                ?.iconColor.length
                ? currentViewJobType?.statuses.find((s) => s.id === statusId)
                    ?.iconColor
                : currentViewJobType?.color,
            color: theme.palette.primary.contrastText,
          },
        }}
        key={title}
      >
        {tableDirection === "row" && (
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            <Base.Badge
              badgeNumber={badgeCount}
              color={currentViewJobType?.color}
            >
              <Grid container direction={"row"} alignItems={"center"}>
                <Base.Text text={title} variant="h3" />
                {currentViewJobType?.statuses.find((s) => s.id === statusId)
                  ?.icon && (
                  <IconSwitcher
                    name={
                      currentViewJobType?.statuses.find(
                        (s) => s.id === statusId,
                      )?.icon as FilledIconName
                    }
                    sx={{
                      fontSize: "25px",
                      color:
                        currentViewJobType?.statuses.find(
                          (s) => s.id === statusId,
                        )?.iconColor ?? currentViewJobType?.color,
                    }}
                  />
                )}
              </Grid>
            </Base.Badge>
          </Box>
        )}
        <Base.AccordionSwitch
          title={title}
          expanded={expanded}
          badgeNumber={badgeCount}
          backgroundColor={theme.palette.background.paper}
          color={currentViewJobType?.color}
          alwaysAccordion={tableDirection === "column"}
          content={
            <Base.TableContainer
              rows={displayRows(jobArray)}
              columns={
                jobArray.some((j) => !!j.siteId)
                  ? [
                      ...siteColumn,
                      ...(jobArray.some((j) => j.completedOn === null) &&
                      (xs || (md && currentViewJobType?.logHours))
                        ? [actionsColumn]
                        : []),
                    ]
                  : [
                      ...titleColumn,
                      ...(jobArray.some((j) => j.completedOn === null)
                        ? [actionsColumn]
                        : []),
                    ]
              }
              totalRows={jobArray.length}
              mobileRows={displayRows(jobArray)}
              pageSize={jobArray.length < 15 ? 15 : jobArray.length}
              loading={status === "loading"}
              onCellClick={onCellClick}
              getCellClassName={(params: GridCellParams<any, any, any>) => {
                return "link";
              }}
              highlightId={addHoursModal || upload.id}
              mobileBackgroundColor="white"
              columnVisibility={{
                created: tableDirection === "column" ? true : false,
                workedBy: tableDirection === "column" ? true : false,
              }}
            />
          }
        />
      </Grid>
    );
  };

  const handleSubmit = (values: AddHoursFormValues) => {
    const newJobTime: AddHoursVM = {
      jobId: job.id,
      categoryId: values?.category?.id,
      note: values.note,
      hours: values.hours?.value,
      workedOn: values.workedOn,
      workedBy: { id: values.workedBy?.id!, value: "" },
    };
    apiAddHours(newJobTime);
    setAddHoursModal(undefined);
  };

  const handleUploadAttachment = (files: File[]) => {
    setFileLength(files.length);
    setNewUpload(
      files.map((f) => {
        return {
          name: f.name,
          upload: f,
          jobId: upload.id,
          attachmentType: undefined,
        };
      }),
    );
  };

  useEffect(() => {
    if (fileLength > 0 && fileLength === newUpload.length && !fileTypeModal) {
      setFileTypeModal(true);
      setUpload({ ...upload, name: "" });
    }
    if (fileLength === 0) {
      setUpload({ id: undefined, name: "" });
    }
  }, [dispatch, fileTypeModal, fileLength, job.id, newUpload]);

  return (
    <Box
      sx={{
        "& .link": {
          cursor: "pointer",
        },

        marginX: { sm: 5, xs: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          justifyContent: { xs: "center", sm: "space-between" },
          alignItems: "center",
          my: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { sm: "row", xs: "column" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconSwitcher
            name={currentViewJobType?.icon as FilledIconName}
            sx={{ fontSize: "25px", color: currentViewJobType?.color }}
          />
          <Base.Text
            text={!!currentViewJobType ? `${currentViewJobType.name}` : ""}
            variant="h3"
          />
          {!sm && (
            <Box sx={{ color: currentViewJobType?.color }}>
              <Base.IconButton
                icon={tableDirection === "row" ? <TableRows /> : <ViewColumn />}
                label={tableDirection === "row" ? "Row View" : "Column View"}
                onClick={() => {
                  if (tableDirection === "row") {
                    setTableDirection("column");
                  } else {
                    setTableDirection("row");
                  }
                }}
                size="large"
              />
            </Box>
          )}
        </Box>
        <Box my={{ xs: 3, md: 0 }}>
          <Base.Button
            text={"New"}
            onClick={() => {
              props.onClickNewJob();
            }}
          />
        </Box>
      </Box>
      {openJobStatus === "loading" || closedJobStatus === "loading" ? (
        <Grid
          container
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Base.Text text="Loading, please wait..." variant="h3" center />
          <CircularProgress />
        </Grid>
      ) : (
        <Grid
          container
          direction={{ xs: "column", md: tableDirection }}
          spacing={2}
          pb={5}
        >
          {openJobsList?.statusJobGroups.map((openStatus, index) =>
            tableDisplay(
              openStatus.jobStatus.value ?? "",
              openStatus.jobGroups,
              index === 0 || !xs,
              openStatus.count ?? 0,
              openStatus.jobStatus.id,
            ),
          )}
          {!!currentViewJobType?.showRecentlyClosed &&
            tableDisplay(
              "Recently Closed (Last 7 Days)",
              completeJobsList?.recentComplete || [],
              xs ? false : true,
              completeJobsList?.recentCompleteCount || 0,
            )}
        </Grid>
      )}
      <Base.Modal open={addHoursModal !== undefined} onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: "fit-content",
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <AddHoursForm
            {...props}
            handleSubmit={handleSubmit}
            handleCancel={() => setAddHoursModal(undefined)}
          />
        </Box>
      </Base.Modal>
      <Base.Modal open={!!viewSites.length} onClose={() => setViewSites([])}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: 300,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Base.Text text="View Sites" center />
          <Divider />
          <Grid container sx={{ maxHeight: 250, overflow: "auto" }}>
            {viewSites.map((s, i) => (
              <Grid size={12} key={i}>
                <Grid
                  container
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Grid>{s.name}</Grid>
                  <Grid>
                    <Room
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        window.open(
                          `http://maps.google.com/?q=${s.address}`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Base.Button text="Close" onClick={() => setViewSites([])} />
        </Box>
      </Base.Modal>
      <Base.Modal
        open={!!upload.id && upload.id > 0 && !!upload.name}
        onClose={() => setUpload({ id: undefined, name: "" })}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: { sm: 500, xs: 300 },
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Grid container>
            <Grid size={12} pb={2}>
              <Base.Text text={`Upload Attachments to ${upload.name}`} center />
              <Divider />
            </Grid>
            <Grid size={12}>
              <Base.Dropzone
                onDrop={handleUploadAttachment}
                disabled={attachmentStatus === "loading"}
              />
            </Grid>
          </Grid>
          <Base.Button
            text="Close"
            onClick={() => setUpload({ id: undefined, name: "" })}
          />
        </Box>
      </Base.Modal>
      <Base.Modal
        open={fileTypeModal}
        onClose={() => {
          setFileTypeModal(false);
          setNewUpload([]);
          setFileLength(0);
        }}
      >
        <JobAttachmentCopy
          id={upload.id!}
          newUpload={newUpload}
          setNewUpload={setNewUpload}
          setFileTypeModal={setFileTypeModal}
          setFileLength={setFileLength}
        />
      </Base.Modal>
    </Box>
  );
};

export default OpenJobs;
